import {
    BooleanInput,
    FunctionField,
    maxValue,
    minValue,
    NumberInput,
    required,
    SimpleForm,
    SimpleShowLayout,
    TextField,
    useRecordContext,
    useTranslate,
} from 'react-admin';
import { DrawerLink } from '@/components/global/drawers/DrawerLink.jsx';
import { StatusField } from '@/components/accessKeys/fields/StatusField.jsx';
import EditableField from '@/components/global/fields/EditableField.jsx';
import { DateInput } from '@/components/global/inputs/DateInput.jsx';
import { useCallback, useState } from 'react';
import { defaultDateFieldOptions } from '@/constants/Date.jsx';
import { validateDaysToRotateUtil, validateExpiresAfterStartUtil, validateNotPastDateUtil } from '@/components/accessKeys/validators/validators.jsx';

const noop = () => {};

const EDITING_FIELDS = {
    CURRENT_KEY_VALID_FROM: 'currentKeyValidFrom',
    CURRENT_KEY_EXPIRES: 'currentKeyExpires',
    AUTO_ROTATION: 'autoRotation',
    DAYS_TO_ROTATE: 'daysToRotate',
};

const MAX_AUTO_DAYS_TO_ROTATE = 30;
const MIN_DAYS_TO_ROTATE = 5;

export const useEditingStates = () => {
    const [currentEditingField, setCurrentEditingField] = useState(null);

    const startEditing = useCallback(field => {
        setCurrentEditingField(field);
    }, []);

    const isEditing = useCallback(
        field => {
            return currentEditingField === field;
        },
        [currentEditingField],
    );

    const resetAllEditingStates = useCallback(() => {
        setCurrentEditingField(null);
    }, []);

    const startEditingCurrentKeyValidFrom = useCallback(() => startEditing(EDITING_FIELDS.CURRENT_KEY_VALID_FROM), [startEditing]);
    const startEditingCurrentKeyExpires = useCallback(() => startEditing(EDITING_FIELDS.CURRENT_KEY_EXPIRES), [startEditing]);
    const startEditingAutoRotation = useCallback(() => startEditing(EDITING_FIELDS.AUTO_ROTATION), [startEditing]);
    const startEditingDaysToRotate = useCallback(() => startEditing(EDITING_FIELDS.DAYS_TO_ROTATE), [startEditing]);

    return {
        isEditing,
        startEditingCurrentKeyValidFrom,
        startEditingCurrentKeyExpires,
        startEditingAutoRotation,
        startEditingDaysToRotate,
        resetAllEditingStates,
    };
};

const calculateDaysToRotateFromDates = (validFrom, expires) => {
    if (!validFrom || !expires) return null;
    const from = new Date(validFrom);
    const to = new Date(expires);
    const diffDays = Math.floor((to - from) / (1000 * 60 * 60 * 24));
    if (diffDays < MIN_DAYS_TO_ROTATE) return null;

    return Math.min(diffDays, MAX_AUTO_DAYS_TO_ROTATE);
};

const buildSave = ({ formData, record }) => {
    const validFrom = formData.currentKeyValidFrom ?? record?.currentKeyValidFrom ?? null;
    const expires = formData.currentKeyExpires ?? record?.currentKeyExpires ?? null;

    if (formData.autoRotation) {
        const calculatedDays = calculateDaysToRotateFromDates(validFrom, expires);
        formData.daysToRotate = calculatedDays;
        if (calculatedDays !== null) {
            formData.daysToRotate = calculatedDays;
        } else if (!formData.daysToRotate) {
            formData.daysToRotate = MAX_AUTO_DAYS_TO_ROTATE;
        }
    } else {
        formData.daysToRotate = null;
    }

    return {
        currentKeyValidFrom: validFrom,
        currentKeyExpires: expires,
        autoRotation: formData.autoRotation,
        daysToRotate: formData.daysToRotate,
    };
};

export const AccessKeySettings = ({ save, editingStates }) => {
    const translate = useTranslate();
    const record = useRecordContext();
    const { isEditing, startEditingCurrentKeyValidFrom, startEditingCurrentKeyExpires, startEditingAutoRotation, startEditingDaysToRotate } = editingStates;

    const handleSave = async formData => {
        const transformedData = buildSave({ formData, record });
        return save(transformedData);
    };

    const validateExpiresAfterStart = useCallback((value, allValues) => validateExpiresAfterStartUtil(value, allValues, translate), [translate]);
    const validateNotPastDate = useCallback(value => validateNotPastDateUtil(value, translate), [translate]);
    const validateDaysToRotate = useCallback((value, allValues) => validateDaysToRotateUtil(value, allValues, translate), [translate]);

    return (
        <SimpleForm toolbar={false} onSubmit={handleSave}>
            <SimpleShowLayout className='details-simple-show-layout'>
                <FunctionField
                    className='serial-number-field'
                    label={translate('resources.accessKey.labels.device')}
                    render={record => <DrawerLink resource='device' action='show' source={record.device.id} label={record.device.serialNumber} relative />}
                />
                <FunctionField label={translate('resources.accessKey.labels.keyPreview')} render={() => <TextField source='keyPreview' />} />
                <DateField
                    label={translate('resources.accessKey.labels.currentKeyValidFrom')}
                    isEditing={isEditing}
                    field='currentKeyValidFrom'
                    onEdit={startEditingCurrentKeyValidFrom}
                    editComponent={
                        <DateInput
                            className='date-input'
                            source='currentKeyValidFrom'
                            label=''
                            name='currentKeyValidFrom'
                            validate={[required(translate('common.validation.required')), validateNotPastDate]}
                        />
                    }
                />
                <DateField
                    label={translate('resources.accessKey.labels.currentKeyExpires')}
                    isEditing={isEditing}
                    field='currentKeyExpires'
                    onEdit={startEditingCurrentKeyExpires}
                    editComponent={
                        <DateInput
                            className='date-input'
                            source='currentKeyExpires'
                            label=''
                            name='currentKeyExpires'
                            validate={[required(translate('common.validation.required')), validateNotPastDate, validateExpiresAfterStart]}
                        />
                    }
                />
                <BooleanField
                    label={translate('resources.accessKey.labels.autoRotation')}
                    isEditing={isEditing}
                    field='autoRotation'
                    onEdit={startEditingAutoRotation}
                    translate={translate}
                />
                <NumberField
                    label={translate('resources.accessKey.labels.daysToRotate')}
                    isEditing={isEditing}
                    field='daysToRotate'
                    onEdit={startEditingDaysToRotate}
                    translate={translate}
                    editComponent={
                        <NumberInput
                            id='daysToRotate'
                            source='daysToRotate'
                            label=''
                            fullWidth
                            autoFocus={isEditing('daysToRotate')}
                            name='daysToRotate'
                            defaultValue={30}
                            validate={[minValue(5), maxValue(365), required(translate('common.validation.required')), validateDaysToRotate]}
                        />
                    }
                />
                <FunctionField label={translate('resources.accessKey.labels.status')} render={record => <StatusField record={record} />} />
                <FunctionField label={translate('resources.accessKey.labels.lastSeenAt')} render={() => <TextField source='lastSeenAt' />} />
            </SimpleShowLayout>
        </SimpleForm>
    );
};
const DateField = ({ field, label, isEditing, onEdit, editComponent }) => (
    <EditableField
        label={label}
        isEditing={isEditing(field)}
        onEdit={isEditing ? onEdit : noop}
        displayValue={record => {
            if (!record[field]) return '-';
            const date = new Date(record[field]);
            return date.toLocaleDateString('pl-PL', defaultDateFieldOptions);
        }}
        isFilled={record => !!record[field]}
        editComponent={editComponent}
        canEdit={true}
        className={isEditing ? 'editable-field--active' : ''}
    />
);

const NumberField = ({ field, label, isEditing, onEdit, translate, editComponent }) => (
    <EditableField
        label={label}
        isEditing={isEditing(field)}
        onEdit={isEditing ? onEdit : noop}
        displayValue={record => record[field] || translate('resources.accessKey.fields.notSpecified')}
        isFilled={record => !!record[field]}
        editComponent={editComponent}
        canEdit={true}
        className={isEditing ? 'editable-field--active' : ''}
    />
);

const BooleanField = ({ field, label, isEditing, onEdit, translate }) => (
    <EditableField
        label={label}
        isEditing={isEditing(field)}
        onEdit={isEditing ? onEdit : noop}
        displayValue={record => translate(`ra.boolean.${record[field]}`)}
        isFilled={record => record[field] !== undefined && record[field] !== null}
        editComponent={<BooleanInput id={field} source={field} label='' fullWidth autoFocus={isEditing(field)} name={field} size='small' />}
        canEdit={true}
        className={isEditing ? 'editable-field--active' : ''}
    />
);
